import {
    tsBulkActionsDelete,
    tsBulkActionsGet,
    tsBulkActionsPatch,
    tsBulkActionsPost,
} from '../../util/api'
import type {
    CustomFieldCatalog,
    CustomFieldDataType,
    CustomFieldDefinition,
    CustomFieldEntityType,
} from './types'

export interface CustomFieldCatalogResponse extends CustomFieldCatalog {
    definition_count: number
    definition_limit: number
    value_count: number
    value_limit: number
}

export interface CustomFieldValue {
    definition_id: string
    entity_id: string
    data_type: CustomFieldDataType
    value: string | boolean
    version: string
    updated_by_user_id: string | null
    updated_at: string
}

export type CustomFieldClearedValue = {
    definition_id: string
    entity_id: string
    value: null
    version: null
}

export interface CustomFieldMutationResult {
    value_count: number
    values: Array<CustomFieldValue | CustomFieldClearedValue>
}

export const isCustomFieldValueSet = (
    item: CustomFieldValue | CustomFieldClearedValue,
): item is CustomFieldValue => item.value !== null

export interface CustomFieldHistoryEntry {
    id: string
    definition_id: string
    field_key: string
    field_name: string
    entity_type: CustomFieldEntityType
    entity_id: string
    data_type: CustomFieldDataType
    operation: 'set' | 'clear'
    value: string | boolean | null
    value_version: string | null
    changed_by_user_id: string | null
    actor_is_admin: boolean
    source_type: 1 | 2 | 3 | 4 | 5
    source_reference: string | null
    created_at: string
}

export interface CustomFieldHistoryPage {
    entries: CustomFieldHistoryEntry[]
    nextCursor: string | null
}

export interface CustomFieldDefinitionValuesPage {
    values: CustomFieldValue[]
    next_cursor: string | null
}

const options = (profileId: string) => ({ headers: { profileid: profileId } })

export const customFieldsApi = {
    catalog: (profileId: string, entityType: CustomFieldEntityType) =>
        tsBulkActionsGet(`custom-fields/catalog/${entityType}`, null, options(profileId)).then(
            (response) => response.data as CustomFieldCatalogResponse,
        ),
    createDefinition: (
        profileId: string,
        input: {
            entity_type: CustomFieldEntityType
            name: string
            description: string | null
            data_type: CustomFieldDataType
        },
    ) =>
        tsBulkActionsPost('custom-fields/definitions', input, options(profileId)).then(
            (response) => response.data.definition as CustomFieldDefinition,
        ),
    updateDefinition: (profileId: string, definition: CustomFieldDefinition) =>
        tsBulkActionsPatch(
            `custom-fields/definitions/${definition.definition_id}`,
            {
                entity_type: definition.entity_type,
                expected_version: definition.version,
                name: definition.name,
                description: definition.description,
            },
            options(profileId),
        ).then((response) => response.data.definition as CustomFieldDefinition),
    deleteDefinition: (
        profileId: string,
        definition: CustomFieldDefinition,
        disableDependants: boolean,
    ) =>
        tsBulkActionsDelete(`custom-fields/definitions/${definition.definition_id}`, null, {
            ...options(profileId),
            data: {
                entity_type: definition.entity_type,
                expected_version: definition.version,
                disable_dependants: disableDependants,
            },
        }),
    values: (profileId: string, entityType: CustomFieldEntityType, entityIds: string[]) =>
        tsBulkActionsGet(
            `custom-fields/values/${entityType}`,
            { entity_ids: entityIds.join(',') },
            options(profileId),
        ).then(
            (response) => response.data.values as Record<string, Record<string, CustomFieldValue>>,
        ),
    definitionValues: (
        profileId: string,
        entityType: CustomFieldEntityType,
        definitionId: string,
        afterEntityId?: string,
        limit = 100,
    ) =>
        tsBulkActionsGet(
            `custom-fields/values/${entityType}/definition/${definitionId}`,
            { after_entity_id: afterEntityId, limit },
            options(profileId),
        ).then((response) => response.data as CustomFieldDefinitionValuesPage),
    snapshotValues: (profileId: string, entityType: CustomFieldEntityType, entityIds: string[]) =>
        tsBulkActionsPost(
            `custom-fields/values/${entityType}/snapshot`,
            { entity_ids: entityIds },
            options(profileId),
        ).then(
            (response) => response.data.values as Record<string, Record<string, CustomFieldValue>>,
        ),
    mutateValues: (
        profileId: string,
        entityType: CustomFieldEntityType,
        mutations: Array<{
            field_definition_id: string
            entity_id: string
            operation: 'set' | 'clear'
            value?: string | boolean
            expected_version?: string | null
        }>,
        sourceReference = 'definition-management-ui',
    ) =>
        tsBulkActionsPost(
            `custom-fields/values/${entityType}/bulk`,
            { mutations, source_reference: sourceReference },
            options(profileId),
        ).then((response) => response.data as CustomFieldMutationResult),
    history: (
        profileId: string,
        entityType: CustomFieldEntityType,
        entityId: string,
        definitionId?: string,
        beforeId?: string,
        limit = 25,
    ) =>
        tsBulkActionsGet(
            `custom-fields/values/${entityType}/${entityId}/history`,
            { definition_id: definitionId, before_id: beforeId, limit },
            options(profileId),
        ).then((response): CustomFieldHistoryPage => {
            const entries = response.data.history as CustomFieldHistoryEntry[]
            return {
                entries,
                nextCursor:
                    entries.length === limit ? (entries[entries.length - 1]?.id ?? null) : null,
            }
        }),
    rollback: (
        profileId: string,
        entityType: CustomFieldEntityType,
        historyId: string,
        expectedVersion: string | null,
    ) =>
        tsBulkActionsPost(
            `custom-fields/history/${historyId}/rollback`,
            { entity_type: entityType, expected_version: expectedVersion },
            options(profileId),
        ).then((response) => response.data),
}

export const customFieldApiError = (error: any): string =>
    error?.response?.data?.error?.message ?? error?.message ?? 'Custom field request failed'
